import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next'; // This package should now be installed
import Header from '../components/Header';
import '../styles/ChatPage.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../hooks/useAuth';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api from '../services/api';
import { Form, Button, Spinner } from 'react-bootstrap'; // This package should now be installed

// Placeholder avatars
import botAvatar from '../assets/logo.png'; // Using logo as bot avatar
import userAvatar from '../assets/user-avatar.png'; // You need to add this image
// import { Spinner } from 'react-bootstrap'; // Using a spinner from react-bootstrap

const ChatPage = () => {
    const { t } = useTranslation();
    const { user } = useAuth(); // Get user info for avatar
    const [query, setQuery] = useState('');
    const [chatHistory, setChatHistory] = useState([]);
    const [isBotLoading, setIsBotLoading] = useState(false);
    const chatBodyRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        if (chatBodyRef.current) {
            chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
        }
    }, [chatHistory]);

    useEffect(() => {
        if (!isBotLoading && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isBotLoading]);

    const handleSendMessage = async (userQuery) => {
        const userMessage = { sender: 'user', text: userQuery };
        const currentChatHistory = [...chatHistory, userMessage];
        
        setChatHistory(currentChatHistory);
        setIsBotLoading(true);
        setQuery(''); // Clear input immediately

        try {
            // Convert React chat history to the format the Python API expects
            const apiChatHistory = currentChatHistory.map(msg => 
                msg.sender === 'user' ? [msg.text, ''] : ['', msg.text]
            );

            const response = await api.post('/doc-search/query', { 
                category: 'own', // Hardcoded as per your example
                question: userQuery,
                chat_history: apiChatHistory,
            });

            // The API returns the full, updated history
            const updatedHistoryFromApi = response.data.chat_history;
            const seenQuestions = new Set();
            const newUiChatHistory = updatedHistoryFromApi
                .map(([q, a]) => [
                    q ? { sender: 'user', text: q } : null,
                    a ? { sender: 'bot', text: a } : null
                ])
                .flat()
                .filter(Boolean)
                .filter(msg => {
                    if (msg.sender === 'user') {
                        if (seenQuestions.has(msg.text)) return false;
                        seenQuestions.add(msg.text);
                    }
                    return true;
                });
            
            setChatHistory(newUiChatHistory);

        } catch (err) {
            const errorMessage = { sender: 'bot', text: 'Sorry, an error occurred.' };
            setChatHistory(prev => [...prev, errorMessage]);
        } finally {
            setIsBotLoading(false);
        }
    };

    const handleQuerySubmit = (e) => {
        e.preventDefault();
        if (!query.trim()) return;
        handleSendMessage(query);
    };

    return (
        <div className="chat-page-container">
            <Header />
            <main className="chat-area" ref={chatBodyRef}>
                {chatHistory.map((msg, index) => (
                    <div key={index} className={`message-group ${msg.sender}`}>
                        <img 
                            src={msg.sender === 'user' ? userAvatar : botAvatar}
                            alt={`${msg.sender} avatar`}
                            className="message-avatar" 
                        />
                        <div className={`chat-bubble ${msg.sender}`}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {msg.text}
                            </ReactMarkdown>
                        </div>
                    </div>
                ))}
                {isBotLoading && (
                     <div className="message-group bot">
                        <img src={botAvatar} alt="Bot avatar" className="message-avatar" />
                        <div className="chat-bubble bot is-loading">
                            <Spinner animation="grow" size="sm" />
                            <Spinner animation="grow" size="sm" />
                            <Spinner animation="grow" size="sm" />
                        </div>
                    </div>
                )}
            </main>
            <footer className="message-input-area">
                <Form onSubmit={handleQuerySubmit} className="w-100 d-flex gap-2">
                    <Form.Control 
                        ref={inputRef}
                        placeholder={t('doc_search_placeholder_chat')} 
                        value={query} 
                        onChange={(e) => setQuery(e.target.value)} 
                        disabled={isBotLoading}
                        autoFocus
                    />
                    <Button type="submit" disabled={isBotLoading || !query.trim()}>
                        <FontAwesomeIcon icon={faPaperPlane} />
                    </Button>
                </Form>
            </footer>
        </div>
    );
};

export default ChatPage;